// deps
import React, { memo, useCallback, useState } from "react";

// types
import { IBillDetails } from "@dineout-payment-success-ui/interfaces";

// styles
import { BillDetailsSubWrapper, BillDetailsWrapper, ChargeBottomSheet, OrderIdText, TipContainer } from "./styles";

// components
import { BillDetailsItem } from "./components/payment-success-bill-details-item";
import { BillDivider } from "./components/payment-success-bill-divider";
import { CartTipSection } from "./components/cart-tip-section";

// helpers
import { BottomSheet, isCurrentSystemThemeDark } from "@swiggy-private/core-ui";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { PAYMENT_SUCCESS_BILL_DETAILS } from "@swiggy-private/payment-test-ids";
import { StyledText } from "../payment-success-banner/styles";
import TipModal from "../tip-modal/TipModal";
import { BILL_ELEMENT_ENTRIES } from "@dineout-payment-success-ui/constants/Constants";
import { CART_BILL_TIP_VALUES } from "@dineout-payment-success-ui/constants/CartTip";
import { useTipAnalytics } from "@dineout-payment-success-ui/hooks/useDineoutPaymentSuccess";

import styles from "./BillDetails.scss";

const BillDetails: React.FC<IBillDetails> = ({
    billEntries,
    title,
    addTip,
    updateTip,
    onImpression,
    isCart,
    maxTipAmount,
    tipAmountExists,
}) => {
    const selectedAmount = tipAmountExists ? tipAmountExists : 0;
    const [tipVisible, setTipVisible] = useState<boolean>(Boolean(Number(selectedAmount) > 0));
    const [tipBottomSheet, setTipBottomSheet] = useState<boolean>(false);
    const [chargesBottomSheet, setChargesBottomSheet] = useState<boolean>(false);
    let defaultTipIndex = null;
    if (CART_BILL_TIP_VALUES.includes(selectedAmount)) {
        defaultTipIndex = CART_BILL_TIP_VALUES.findIndex((val) => val === selectedAmount);
    } else if (selectedAmount > 0) {
        defaultTipIndex = 3;
    }
    const [selectedTipIndex, setSelectedTipIndex] = useState(defaultTipIndex || null);

    const { onCustomTipNextClick, onClearTipClick, onClickTipPredefined, onClickCustomTip, onTipClicked } =
        useTipAnalytics();

    useEffectOnce(() => {
        onImpression?.();
    });

    const [tipValue, setTipValue] = useState<number>(0);

    const clearTip = (): void => {
        onClearTipClick(0);
        setTipValue(0);
        updateTip && updateTip(0);
        setSelectedTipIndex(null);
    };

    const addTipClicked = (): void => {
        onTipClicked();
        if (addTip) {
            setTipVisible(!tipVisible);
        }
    };

    const onOtherTipConfirmed = (data: number) => {
        onCustomTipNextClick(data);
        setTipValue(data);
        updateTip && updateTip(data);
        setTipBottomSheet(false);
    };

    const onTipSelected = (data: number | string, index: number): void => {
        if (index === selectedTipIndex && typeof data === "number") {
            clearTip();
            return;
        }

        setSelectedTipIndex(index);
        if (typeof data === "number") {
            onClickTipPredefined(data);
            setTipValue(data);
            updateTip && updateTip(data);
            setTipBottomSheet(false);
        } else {
            onClickCustomTip();
            setTipBottomSheet(true);
        }
    };
    const onCloseHandler = useCallback(() => {
        setTipBottomSheet(false);
        setChargesBottomSheet(false);
    }, []);
    const [toolTipText, setToolTipText] = useState<string>("");
    const [toolTipTitle, setToolTipTitle] = useState<string>("");
    const toolTipClicked = (title: string, text: string) => {
        setChargesBottomSheet(true);
        setToolTipText(text);
        setToolTipTitle(title);
    };
    const getLeftText = (text: string, id: string) => {
        if (isCart && text && id) {
            if (id === "billToPay") {
                return `${text} <em>(Rounded off)</em>`;
            }
            return text;
        }
        return text;
    };
    return (
        <BillDetailsWrapper>
            <OrderIdText
                variant="V2.body1.neutral.bold"
                color="text_High_Emphasis"
                data-testid={PAYMENT_SUCCESS_BILL_DETAILS.ORDER_ID}
            >
                {title}
            </OrderIdText>
            <BillDetailsSubWrapper>
                {billEntries?.map((item, index) => {
                    const showDivider = item.isBillDivider || index === billEntries.length - 2;
                    if (item?.leftElement === null && item?.rightElement === null) {
                        return;
                    }

                    if (item.id == BILL_ELEMENT_ENTRIES.ADD_TIP_CART_ID && addTip) {
                        return (
                            <React.Fragment key={item.id}>
                                <CartTipSection
                                    item={item}
                                    addTip={addTip}
                                    tipVisible={tipVisible}
                                    tipValue={tipValue}
                                    selectedTipIndex={selectedTipIndex}
                                    onAddTipClick={addTipClicked}
                                    onClearTip={clearTip}
                                    onTipSelected={onTipSelected}
                                />
                            </React.Fragment>
                        );
                    }

                    return (
                        <React.Fragment key={item.id}>
                            <BillDetailsItem
                                id={item.id}
                                leftText={getLeftText(item.leftElement?.text ?? "", item.id || "")}
                                leftTextStyle={{
                                    variant: item.leftElement?.variant,
                                    color: item.leftElement?.color,
                                }}
                                rightText={item.rightElement?.text ?? ""}
                                rightTextStyle={{
                                    variant: item.rightElement?.variant,
                                    color: item.rightElement?.color,
                                }}
                                isClickable={item?.leftElement?.isClickable ? item?.leftElement?.isClickable : false}
                                offerUrl={item.offerLogo ?? ""}
                                offerLogoStyle={item.offerLogoStyle}
                                toolTipClicked={() => {
                                    toolTipClicked(
                                        item?.leftElement?.toolTipTitle || "",
                                        item?.leftElement?.toolTipText || "",
                                    );
                                }}
                                strikethroughAmount={item?.strikethroughAmount}
                                leftSubText={item?.leftSubText}
                                leftSubTextStyles={item?.leftSubTextStyles}
                            />
                            {showDivider && <BillDivider />}
                        </React.Fragment>
                    );
                })}
            </BillDetailsSubWrapper>
            {tipBottomSheet && (
                <TipContainer id={BILL_ELEMENT_ENTRIES.ADD_TIP_MODAL}>
                    <TipModal
                        onConfirm={onOtherTipConfirmed}
                        maxTipAmount={maxTipAmount}
                        close={() => setTipBottomSheet(false)}
                    />
                </TipContainer>
            )}
            {chargesBottomSheet && (
                <BottomSheet
                    containerClass={isCurrentSystemThemeDark() ? styles.darkThemeContainer : styles.lightThemeContainer}
                    onClose={onCloseHandler}
                    hideOnOverlayClick={true}
                    hideCloseIcon={false}
                >
                    {(): React.ReactElement => (
                        <ChargeBottomSheet>
                            <StyledText
                                variant="V2.body2.neutral.medium"
                                color="text_High_Emphasis"
                                data-testid="TIPPING_AMOUNT"
                            >
                                {toolTipTitle}
                            </StyledText>
                            <StyledText
                                variant="V2.body2.neutral.regular"
                                color="text_Med_Emphasis"
                                textAlign="left"
                                data-testid="TIPPING_AMOUNT"
                            >
                                {toolTipText}
                            </StyledText>
                        </ChargeBottomSheet>
                    )}
                </BottomSheet>
            )}
        </BillDetailsWrapper>
    );
};

export default memo(BillDetails);
